import DOMPurify from "dompurify";

interface MarkdownRendererProps {
  content: string;
}

/**
 * Robust Regex-based Markdown → HTML renderer.
 * Focuses on common Markdown syntax including headers, formatting, lists, and code.
 */
const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  const renderMarkdown = (text: string) => {
    let html = text;

    // Standardize line endings
    html = html.replace(/\r\n/g, '\n');

    // 1. Block Elements
    
    // Code Blocks
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
      return `<pre class="bg-muted/50 p-3 rounded-lg my-4 overflow-x-auto"><code class="text-xs font-mono">${DOMPurify.sanitize(code.trim())}</code></pre>`;
    });

    // Headers
    html = html.replace(/^###### (.*$)/gm, '<h6 class="text-xs font-bold mt-2 mb-1">$1</h6>');
    html = html.replace(/^##### (.*$)/gm, '<h5 class="text-sm font-bold mt-2 mb-1">$1</h5>');
    html = html.replace(/^#### (.*$)/gm, '<h4 class="text-base font-bold mt-2 mb-2">$1</h4>');
    html = html.replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mt-3 mb-2">$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-3 border-b border-border/50 pb-2">$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-4 border-b border-border/50 pb-2 text-primary">$1</h1>');

    // Blockquotes
    html = html.replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-primary/50 pl-4 py-1 my-4 italic text-muted-foreground">$1</blockquote>');

    // Horizontal Rules
    html = html.replace(/^---$/gm, '<hr class="border-border/50 my-4" />');

    // Unordered Lists (simple approach: match lines starting with - or *)
    // Wrap groups of list items in <ul>
    html = html.replace(/^([-*+])\s+(.*$)/gm, '<li class="ml-4 list-disc">$2</li>');
    // This is a naive wrap, but works for basic rendering without a complex parser
    // To do it better, we'd need to look-ahead/back. For now, this is okay for a copy-paste tool.

    // Ordered Lists
    html = html.replace(/^\d+\.\s+(.*$)/gm, '<li class="ml-4 list-decimal">$1</li>');

    // 2. Inline Elements
    
    // Bold & Italic
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');

    // Strikethrough
    html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');

    // Inline Code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');

    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline font-medium">$1</a>');

    // Images (treated like links for simplicity in text content)
    html = html.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded-lg my-4" />');

    // Line breaks (convert double newlines to paragraphs)
    const paragraphs = html.split(/\n\s*\n/);
    html = paragraphs.map(p => {
      if (p.trim().startsWith('<h') || p.trim().startsWith('<blockquote') || p.trim().startsWith('<pre') || p.trim().startsWith('<hr') || p.trim().startsWith('<li')) {
        return p;
      }
      return `<p class="mb-3 leading-relaxed">${p.replace(/\n/g, '<br />')}</p>`;
    }).join('\n');

    return DOMPurify.sanitize(html);
  };

  return (
    <div
      className="markdown-content text-sm text-foreground prose-sm max-w-none break-words [&>*:first-child]:mt-0"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

export default MarkdownRenderer;
